import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { diaryAPI } from '../services/api';
import { useProfile } from '../context/ProfileContext';

const MOODS = [
  { id: 'happy', emoji: '😊', label: 'Happy' },
  { id: 'excited', emoji: '🤩', label: 'Excited' },
  { id: 'calm', emoji: '😌', label: 'Calm' },
  { id: 'sad', emoji: '😢', label: 'Sad' },
  { id: 'tired', emoji: '😴', label: 'Tired' },
  { id: 'angry', emoji: '😠', label: 'Angry' }
];

export default function DiaryScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const [view, setView] = useState('list'); // 'list' | 'new'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', mood: 'happy', weather: '☀️' });
  const [saved, setSaved] = useState(false);

  const WEATHER_OPTIONS = ['☀️', '⛅', '🌧️', '❄️', '🌈', '🌙'];

  useEffect(() => {
    if (profile) {
      diaryAPI.getAll(profile.id)
        .then(data => { setEntries(data); setLoading(false); })
        .catch(() => setLoading(false));
    } else { setLoading(false); }
  }, [profile]);

  const handleSave = async () => {
    if (!profile || !form.content.trim()) return;
    setSaving(true);
    try {
      const result = await diaryAPI.create({ profileId: profile.id, ...form });
      setEntries(prev => [result.entry, ...prev]);
      refreshProfile();
      setSaved(true);
      setForm({ title: '', content: '', mood: 'happy', weather: '☀️' });
      setView('list');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Could not save entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #2BCBBA, #00B5A5)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => view === 'new' ? setView('list') : navigate('/')} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
        <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.5rem', flex: 1 }}>📖 My Diary</h1>
        {view === 'list' && (
          <button className="btn btn-sm" onClick={() => setView('new')} style={{ background: 'rgba(255,255,255,0.9)', color: '#2BCBBA', fontWeight: 800 }}>
            ✏️ Write
          </button>
        )}
      </div>

      <div className="screen-body" style={{ paddingTop: 20 }}>
        {view === 'new' ? (
          <div>
            <h2 style={{ fontFamily: 'var(--font-title)', fontSize: '1.2rem', marginBottom: 16 }}>
              How are you today? 🌟
            </h2>

            {/* Mood Selector */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 800, fontSize: '0.85rem', display: 'block', marginBottom: 8 }}>How do you feel?</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {MOODS.map(mood => (
                  <button key={mood.id} onClick={() => setForm(f => ({ ...f, mood: mood.id }))} style={{
                    padding: '8px 14px', border: '3px solid',
                    borderColor: form.mood === mood.id ? 'var(--teal)' : 'var(--border)',
                    borderRadius: 14, background: form.mood === mood.id ? '#E0F7FA' : 'white',
                    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700,
                    fontFamily: 'var(--font-body)'
                  }}>
                    {mood.emoji} {mood.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Weather */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 800, fontSize: '0.85rem', display: 'block', marginBottom: 8 }}>Weather today</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {WEATHER_OPTIONS.map(w => (
                  <button key={w} onClick={() => setForm(f => ({ ...f, weather: w }))} style={{
                    width: 44, height: 44, border: '3px solid',
                    borderColor: form.weather === w ? 'var(--teal)' : 'var(--border)',
                    borderRadius: 12, background: form.weather === w ? '#E0F7FA' : 'white',
                    cursor: 'pointer', fontSize: '1.3rem'
                  }}>{w}</button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 800, fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>Title (optional)</label>
              <input className="input" placeholder="What happened today?" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>

            {/* Content */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 800, fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>Write about your day</label>
              <textarea className="input" placeholder="Today was..." value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} style={{ minHeight: 200 }} />
            </div>

            <button className="btn btn-primary btn-full btn-lg" onClick={handleSave} disabled={saving || !form.content.trim()}>
              {saving ? 'Saving...' : '💾 Save My Entry (+4 ⭐)'}
            </button>
          </div>
        ) : (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '4rem', marginBottom: 12 }}>📔</div>
                <h3 style={{ fontFamily: 'var(--font-title)', marginBottom: 8 }}>Your diary is empty</h3>
                <p style={{ color: 'var(--gray)', marginBottom: 20 }}>Write about your day to remember it forever!</p>
                <button className="btn btn-primary" onClick={() => setView('new')}>✏️ Write Today</button>
              </div>
            ) : (
              entries.map(entry => (
                <div key={entry.id} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontWeight: 800 }}>{MOODS.find(m => m.id === entry.mood)?.emoji} {entry.title || 'My Entry'}</div>
                    <div style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>{entry.weather} {new Date(entry.date).toLocaleDateString()}</div>
                  </div>
                </div>
              ))
            )}
            {saved && <div className="points-popup">⭐ +4 points! Diary entry saved!</div>}
          </>
        )}
      </div>
    </div>
  );
}
